/**
 * Small edits the toolbar and the panels both make.
 *
 * Kept in one place because the same button now appears in two - the floating
 * toolbar and the settings panel - and two copies of "change the font" are two
 * chances for one of them to forget that a family may have no bold face.
 */

import type { Colour, GraphicLayer, Layer, TextLayer } from '../model/types';
import { isGradient } from '../model/types';
import { toCss } from '../model/units';
import { useEditor } from '../state/store';
import { familyHas } from '../text/fonts';

/** One field of one layer, as one undo step per gesture. */
export function setField<T extends Layer, K extends keyof T>( layer: T, field: K, value: T[ K ], label = 'Change' ) {
  useEditor.getState().updateLayer( layer.id, label, ( l ) => ( { ...( l as T ), [ field ]: value } ), {
    coalesce: `${ layer.id }:${ String( field ) }`,
  } );
}

/**
 * A new family, keeping the look where the family can hold it.
 *
 * Switching to a family with no italic must not leave the layer asking for
 * one forever, and a family with no bold falls back to regular rather than to
 * a bold the browser would fake and the press would not print.
 */
export function changeFamily( layer: TextLayer, family: string ) {
  const stock = familyHas( family );

  setField( layer, 'font', {
    ...layer.font,
    family,
    weight: layer.font.weight >= 600 && ! stock.bold ? 400 : layer.font.weight,
    style: layer.font.style === 'italic' && ! stock.italic ? 'normal' : layer.font.style,
  }, 'Font' );
}

/** The three case changes, as they apply to the text itself. */
export const CASES: Array<{ label: string; title: string; change: ( text: string ) => string }> = [
  { label: 'AB', title: 'UPPER CASE', change: ( t ) => t.toUpperCase() },
  { label: 'ab', title: 'lower case', change: ( t ) => t.toLowerCase() },
  {
    label: 'Ab',
    title: 'Title Case',
    change: ( t ) => t.replace( /\S+/g, ( word ) => word.charAt( 0 ).toUpperCase() + word.slice( 1 ).toLowerCase() ),
  },
];

/** The next alignment round the cycle the toolbar's one button steps through. */
export function nextAlign( align: TextLayer[ 'align' ] ): TextLayer[ 'align' ] {
  const order: Array<TextLayer[ 'align' ]> = [ 'left', 'center', 'right', 'justify' ];

  return order[ ( order.indexOf( align ) + 1 ) % order.length ] ?? 'left';
}

/* ---------------------------------------------------------------
 * Drawings
 * ------------------------------------------------------------ */

/**
 * The one ink a drawing is in, if it is in one.
 *
 * An icon from the library is a single colour drawn as a dozen paths, so the
 * swatch in the toolbar has to look past the paths to answer "what colour is
 * this?". A drawing in several colours has no single answer, and says so by
 * returning nothing.
 */
export function graphicInk( layer: GraphicLayer ): Colour | null {
  let found: Colour | null = null;

  for ( const path of layer.paths ) {
    for ( const ink of [ path.stroke ? path.stroke.colour : null, isGradient( path.fill ) ? null : path.fill ] ) {
      if ( ! ink ) continue;

      if ( ! found ) {
        found = ink;

        continue;
      }

      if ( toCss( found ) !== toCss( ink ) ) return null;
    }
  }

  return found;
}

/**
 * Recolours every path in a drawing.
 *
 * Fill stays fill and outline stays outline: a solid icon that became an
 * outlined one because it was recoloured would be a strange thing to have
 * happen. Only the ink changes.
 */
export function recolourGraphic( layer: GraphicLayer, colour: Colour ) {
  useEditor.getState().updateLayer(
    layer.id,
    'Colour',
    ( l ) => {
      const graphic = l as GraphicLayer;

      return {
        ...graphic,
        paths: graphic.paths.map( ( path ) => ( {
          ...path,
          fill: path.fill ? colour : null,
          stroke: path.stroke ? { ...path.stroke, colour } : null,
        } ) ),
      } as Layer;
    },
    { coalesce: `${ layer.id }:graphic-colour` }
  );
}

/**
 * How thick a drawing's lines print, in millimetres.
 *
 * A path's width is kept as a fraction of its box, so that scaling an icon
 * scales its lines with it (press/draw.ts multiplies by the same geometric
 * mean). What the customer sets is millimetres on paper; the conversion lives
 * here and nowhere else.
 */
export function graphicWidthMm( layer: GraphicLayer ): number | null {
  const across = Math.sqrt( layer.w * layer.h );
  const widths = layer.paths.filter( ( p ) => p.stroke ).map( ( p ) => ( p.stroke as { width: number } ).width * across );

  if ( widths.length === 0 ) return null;

  return Math.round( Math.max( ...widths ) * 100 ) / 100;
}

/**
 * Sets the thickness of every line in a drawing.
 *
 * Proportionally, so a drawing whose lines were deliberately of two weights
 * keeps the difference: the thickest line becomes the millimetres asked for
 * and the rest follow.
 */
export function setGraphicWidth( layer: GraphicLayer, mm: number ) {
  const now = graphicWidthMm( layer );

  if ( ! now || now <= 0 ) return;

  const scale = Math.max( 0.02, mm ) / now;

  useEditor.getState().updateLayer(
    layer.id,
    'Line width',
    ( l ) => {
      const graphic = l as GraphicLayer;

      return {
        ...graphic,
        paths: graphic.paths.map( ( path ) =>
          path.stroke
            ? { ...path, stroke: { ...path.stroke, width: path.stroke.width * scale } }
            : path
        ),
      } as Layer;
    },
    { coalesce: `${ layer.id }:graphic-width` }
  );
}
